<template>
  <div class="ts-root" ref="container">
    <!-- Trigger area: chips + input -->
    <div :class="['ts-trigger', { 'ts-focus': open }]" @click="focusInput">
      <!-- Multi mode: chips -->
      <template v-if="!single">
        <span
          v-for="tag in selectedTags"
          :key="tag._id"
          class="ts-chip"
        >
          {{ tag.name }}
          <span @click.stop="remove(tag._id)" class="ts-chip-x">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </span>
        </span>
      </template>
      <!-- Single mode: selected label -->
      <template v-else>
        <span v-if="selectedSingleTag" class="ts-single-value">{{ selectedSingleTag.name }}</span>
      </template>
      <input
        ref="input"
        v-model="query"
        type="text"
        :placeholder="currentPlaceholder"
        class="input-plain ts-input"
        @focus="onFocus"
        @keydown.backspace="onBackspace"
        @keydown.esc="open = false"
      />
      <!-- Single mode: clear + chevron -->
      <span v-if="single" class="ts-actions">
        <span v-if="modelValue && clearable" @click.stop="clear" class="ts-clear">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </span>
        <svg class="ts-chevron" :class="{ 'ts-chevron-up': open }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
      </span>
    </div>

    <!-- Dropdown -->
    <Teleport to="body">
    <Transition name="ts-dd">
      <div v-if="open && (filteredOptions.length || loading || canCreate)" class="ts-dropdown" :style="dropdownStyle">
        <div v-if="loading" class="ts-empty"><div class="ts-spinner" /></div>
        <template v-else>
          <div
            v-for="option in filteredOptions"
            :key="option._id"
            @click="add(option)"
            :class="['ts-option', { 'ts-option-active': single && isSelectedSingle(option._id) }]"
          >
            <span class="ts-option-label">{{ option.name }}</span>
            <svg v-if="single && isSelectedSingle(option._id)" class="ts-option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <div v-if="canCreate" @click="createTag" class="ts-option ts-option-create">
            <span class="ts-option-label">Create "<strong>{{ query.trim() }}</strong>"</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </div>
          <div v-if="!filteredOptions.length && !canCreate" class="ts-empty">No tags found</div>
        </template>
      </div>
    </Transition>
    </Teleport>
  </div>
</template>

<script>
import api from '../services/api.js'

export default {
  name: 'TagSelect',
  props: {
    modelValue: { type: [Array, String, Object], default: () => [] },
    placeholder: { type: String, default: 'Select tags...' },
    single: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    preload: { type: Boolean, default: false },
    allowCreate: { type: Boolean, default: true }
  },
  emits: ['update:modelValue'],

  data() {
    return {
      open: false,
      query: '',
      tags: [],
      loaded: false,
      loading: false,
      ddPos: {}
    }
  },

  computed: {
    // Multi mode
    selectedTags() {
      if (this.single) return []
      const ids = Array.isArray(this.modelValue) ? this.modelValue : []
      return this.tags.filter(t => ids.includes(t._id))
    },
    // Single mode
    selectedSingleTag() {
      if (!this.single || !this.modelValue) return null
      return this.tags.find(t => t._id === this.modelValue || t._id?.toString() === this.modelValue?.toString())
    },
    dropdownStyle() {
      return this.ddPos
    },
    filteredOptions() {
      let items
      if (this.single) {
        items = [...this.tags]
      } else {
        const ids = Array.isArray(this.modelValue) ? this.modelValue : []
        items = this.tags.filter(t => !ids.includes(t._id))
      }
      if (this.query) {
        const q = this.query.toLowerCase()
        items = items.filter(t => t.name?.toLowerCase().includes(q))
      }
      return items
    },
    canCreate() {
      if (!this.allowCreate || !this.query?.trim() || this.loading) return false
      const q = this.query.trim().toLowerCase()
      return !this.tags.some(t => t.name?.toLowerCase() === q)
    },
    currentPlaceholder() {
      if (this.single) return this.selectedSingleTag ? '' : this.placeholder
      return this.selectedTags.length ? '' : this.placeholder
    }
  },

  watch: {
    open(val) {
      if (val) {
        if (!this.loaded) this.loadTags()
        this.updateDropdownPos()
        document.addEventListener('mousedown', this.onClickOutside)
        window.addEventListener('scroll', this.onScroll, true)
      } else {
        document.removeEventListener('mousedown', this.onClickOutside)
        window.removeEventListener('scroll', this.onScroll, true)
      }
    },
    modelValue(val) {
      const hasValue = this.single ? !!val : (Array.isArray(val) && val.length > 0)
      if (hasValue && !this.loaded) this.loadTags()
    }
  },

  mounted() {
    if (this.preload && !this.loaded) { this.loadTags(); return }
    const hasValue = this.single ? !!this.modelValue : (Array.isArray(this.modelValue) && this.modelValue.length > 0)
    if (hasValue && !this.loaded) this.loadTags()
  },

  beforeUnmount() {
    document.removeEventListener('mousedown', this.onClickOutside)
    window.removeEventListener('scroll', this.onScroll, true)
  },

  methods: {
    onScroll(e) {
      if (e.target.closest && e.target.closest('.ts-dropdown')) return
      this.open = false
    },

    updateDropdownPos() {
      this.$nextTick(() => {
        const el = this.$refs.container
        if (!el) return
        const rect = el.getBoundingClientRect()
        const dropdownHeight = 240
        const spaceBelow = window.innerHeight - rect.bottom
        const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight

        if (openAbove) {
          this.ddPos = {
            position: 'fixed',
            bottom: (window.innerHeight - rect.top + 5) + 'px',
            left: rect.left + 'px',
            width: rect.width + 'px',
            zIndex: 9999
          }
        } else {
          this.ddPos = {
            position: 'fixed',
            top: rect.bottom + 5 + 'px',
            left: rect.left + 'px',
            width: rect.width + 'px',
            zIndex: 9999
          }
        }
      })
    },

    async loadTags() {
      this.loading = true
      try {
        const res = await api.get('/tags', { silent: true })
        this.tags = res.data?.items || []
        this.loaded = true
      } catch { /* silent */ } finally {
        this.loading = false
      }
    },

    focusInput() {
      this.$refs.input?.focus()
    },

    onFocus() {
      this.open = true
      this.query = ''
    },

    add(option) {
      if (this.single) {
        this.$emit('update:modelValue', option._id)
        this.open = false
        this.query = ''
      } else {
        const ids = Array.isArray(this.modelValue) ? this.modelValue : []
        this.$emit('update:modelValue', [...ids, option._id])
        this.query = ''
        this.$refs.input?.focus()
      }
    },

    async createTag() {
      const name = this.query.trim()
      if (!name) return
      this.query = ''
      try {
        const res = await api.post('/tags', { tag: { name } })
        const newTag = res.data?.tag
        if (newTag?._id) {
          this.tags = [...this.tags, newTag]
          const ids = Array.isArray(this.modelValue) ? [...this.modelValue] : []
          ids.push(newTag._id)
          this.$emit('update:modelValue', ids)
          this.query = ''
          await this.$nextTick()
          this.$refs.input?.focus()
        }
      } catch {
        // silent
      }
    },

    remove(id) {
      if (this.single) {
        this.$emit('update:modelValue', null)
      } else {
        const ids = Array.isArray(this.modelValue) ? this.modelValue : []
        this.$emit('update:modelValue', ids.filter(v => v !== id))
      }
    },

    clear() {
      this.$emit('update:modelValue', this.single ? null : [])
      this.query = ''
    },

    isSelectedSingle(id) {
      return this.modelValue && (this.modelValue === id || this.modelValue?.toString() === id?.toString())
    },

    onBackspace() {
      if (this.single) return
      const ids = Array.isArray(this.modelValue) ? this.modelValue : []
      if (!this.query && ids.length) {
        this.$emit('update:modelValue', ids.slice(0, -1))
      }
    },

    onClickOutside(e) {
      if (this.$refs.container && !this.$refs.container.contains(e.target) && !e.target.closest('.ts-dropdown')) {
        this.open = false
      }
    },

    getTagName(id) {
      if (!id) return ''
      const tag = this.tags.find(t => t._id === id || t._id?.toString() === id?.toString())
      return tag?.name || ''
    }
  }
}
</script>

<style scoped>
.ts-root {
  position: relative;
}

/* ── Trigger ── */
.ts-trigger {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 42px;
  padding: 6px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
  cursor: text;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.ts-trigger:hover { border-color: #d1d5db; }
.ts-trigger.ts-focus { border-color: var(--color-primary); box-shadow: 0 0 0 3px var(--color-primary-glow); }

/* ── Chip ── */
.ts-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
  background: var(--color-primary);
  color: #fff;
  white-space: nowrap;
  flex-shrink: 0;
}
.ts-chip-x {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity 0.1s, background 0.1s;
}
.ts-chip-x:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.2);
}

/* ── Single mode value ── */
.ts-single-value {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Actions (single mode) ── */
.ts-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; margin-left: auto; }
.ts-clear {
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 6px;
  color: #b0b0b0; cursor: pointer; transition: background 0.1s, color 0.1s;
}
.ts-clear:hover { background: #f3f4f6; color: #6b7280; }
.ts-chevron { color: #9ca3af; transition: transform 0.2s; }
.ts-chevron-up { transform: rotate(180deg); }

/* ── Input ── */
.ts-input {
  flex: 1;
  min-width: 80px;
  height: 28px;
  border: none;
  outline: none;
  font-size: 13px;
  font-family: 'Poppins', sans-serif;
  color: #1f2937;
  background: transparent;
}
.ts-input::placeholder { color: #c0c0c0; }

/* ── Dropdown ── */
.ts-dropdown {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
}
.ts-dropdown::-webkit-scrollbar { width: 6px; }
.ts-dropdown::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 3px; }

/* ── Option ── */
.ts-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.1s;
}
.ts-option:hover { background: #f5f7fa; }
.ts-option-active { background: rgba(51, 147, 255, 0.06); }
.ts-option-active:hover { background: rgba(51, 147, 255, 0.09); }
.ts-option-label {
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
  font-family: 'Poppins', sans-serif;
}
.ts-option-active .ts-option-label { color: var(--color-primary); font-weight: 600; }
.ts-option-check { flex-shrink: 0; }
.ts-option-create { border-top: 1px solid #f3f4f6; }
.ts-option-create .ts-option-label { color: var(--color-primary); }

/* ── Empty / spinner ── */
.ts-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-size: 13px;
  color: #9ca3af;
}
.ts-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #e5e7eb;
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: ts-spin 0.6s linear infinite;
}
@keyframes ts-spin { to { transform: rotate(360deg); } }

/* ── Transitions ── */
.ts-dd-enter-active { transition: opacity 0.12s ease, transform 0.12s ease; }
.ts-dd-leave-active { transition: opacity 0.08s ease, transform 0.08s ease; }
.ts-dd-enter-from, .ts-dd-leave-to { opacity: 0; transform: translateY(-3px); }
</style>
